import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { qualityNcrAPI } from '@/lib/api';

const Row = ({ r, onSaved, isAdmin }) => {
  const [name, setName] = useState(r.name);
  const [active, setActive] = useState(r.active);
  const dirty = name !== r.name || active !== r.active;
  const save = async () => {
    try { await qualityNcrAPI.updateReason(r.id, { name, active }); toast.success('Reason updated'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="flex items-center gap-3 py-1.5 border-b last:border-0" data-testid={`reason-row-${r.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!isAdmin} className="h-8 max-w-md" data-testid={`reason-name-${r.id}`} />
      <Switch checked={active} onCheckedChange={setActive} disabled={!isAdmin} data-testid={`reason-active-${r.id}`} />
      <span className="text-xs text-muted-foreground w-14">{active ? 'Active' : 'Hidden'}</span>
      {isAdmin && dirty && <Button size="sm" variant="outline" className="h-8" onClick={save} data-testid={`reason-save-${r.id}`}><Save className="w-3.5 h-3.5 mr-1" />Save</Button>}
    </div>
  );
};

export default function RejectionReasonsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const load = useCallback(async () => {
    try { setRows((await qualityNcrAPI.reasons({ include_inactive: true })).data); } catch { toast.error('Failed to load reasons'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setAdding(true);
    try { await qualityNcrAPI.addReason({ name }); setName(''); toast.success('Reason added'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setAdding(false); }
  };
  return (
    <div className="space-y-4 max-w-3xl" data-testid="rejection-reasons-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight">Rejection Reasons</h1>
        <p className="text-xs text-muted-foreground">The select list used on customer complaints and internal NCRs. {isAdmin ? 'Hide a reason to stop new use; existing records keep it.' : 'Only admins can change this list.'}</p>
      </div>
      {isAdmin && (
        <Card className="shadow-none"><CardContent className="p-3 flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim() && add()} placeholder="New rejection reason" data-testid="reason-new-input" />
          <Button onClick={add} disabled={!name.trim() || adding} data-testid="reason-add-btn">{adding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Add</Button>
        </CardContent></Card>
      )}
      <Card className="shadow-none">
        <CardHeader className="pb-1"><CardTitle className="text-sm">{rows.length} reasons</CardTitle></CardHeader>
        <CardContent>{rows.map((r) => <Row key={`${r.id}-${r.name}-${r.active}`} r={r} onSaved={load} isAdmin={isAdmin} />)}</CardContent>
      </Card>
    </div>
  );
}
